/* eslint-disable no-use-before-define */
/* eslint-disable no-unused-vars */
import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import axios from "axios";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import { LocationDenialUnassign } from "@components/Modals";
import { JurisdictionLocationService } from "@services";
import utils from "@utils";
import {
	LOCATION_APPROVAL_TYPES,
	LOCATION_APPROVAL_TYPES_MAP,
	LOCATION_DENIAL_UNASSIGN_ACTIONS,
} from "@constants";
import Actions from "@redux/admin";
import { Link } from "react-router";
import { HeaderGrid, LocationModal, ScrollDetect, Image, SearchField } from "../../../components";

import "./LocationList.scss";

let cancel;

function LocationList({ router, user, setModalOpen, setModalSettings }) {
	if (user.IsInternalCoordinator) window.location.href = "/";
	const { lang } = global;
	const {
		location: { query },
	} = router;
	const displayCount = 25;
	const [locations, setLocations] = useState([]);
	const [hasMore, setHasMore] = useState(true);
	const [pageNumber, setPageNumber] = useState(1);
	const [visible, setVisible] = useState(false);
	const [showLocationModal, setShowLocationModal] = useState(false);

	const [counts, setCounts] = useState({
		Approved: 0,
		Denied: 0,
		Pending: 0,
	});
	const [loadingCounts, setLoadingCounts] = useState(true);
	const [filters, setFilters] = useState({
		search: query.search || "",
		status: query.status || LOCATION_APPROVAL_TYPES.pending.type,
	});

	const [selections, setSelections] = useState([]);

	/** Retrieves a paginated list of locations */
	const getLocations = () => {
		// If there is a request already running, abort it and start a new one
		if (cancel) {
			cancel();
		}

		const filteredParams = Object.fromEntries(
			Object.entries(filters).filter(item => item[1] !== ""),
		);
		const queryParams = {
			pageNumber,
			displayCount,
			...filteredParams,
		};

		JurisdictionLocationService.getAll({
			queryParams,
			getCancelToken: c => {
				cancel = c;
			},
		})
			.then(({ data }) => {
				cancel = undefined;
				const newLocations = data?.ItemList ?? [];

				setLocations(pageNumber > 1 ? [...locations, ...newLocations] : newLocations);
				setPageNumber(pageNumber + 1);
				setHasMore(newLocations.length >= displayCount);
			})
			.catch(error => {
				if (!axios.isCancel(error)) {
					setLocations([]);
					setPageNumber(1);
					setHasMore(false);
					utils.notify({
						type: "error",
						message: lang.t("Error getting LEA Locations"),
					});
				}
			});
	};

	/** Retrieves a count of location types */
	const getCounts = () => {
		JurisdictionLocationService.getCounts()
			.then(({ data }) => {
				setCounts({
					Approved: data.Approved,
					Denied: data.Denied,
					Pending: data.Pending,
				});
				if (loadingCounts) setLoadingCounts(false);
			})
			.catch(error => {
				if (!axios.isCancel(error)) {
					setCounts({
						Approved: 0,
						Denied: 0,
						Pending: 0,
					});
					if (loadingCounts) setLoadingCounts(false);
					utils.notify({
						type: "error",
						message: lang.t("Error getting LEA Locations Counts"),
					});
				}
			});
	};

	useEffect(() => {
		getCounts();
		return () => {
			if (cancel) cancel();
		};
	}, []);

	useEffect(() => {
		if (visible) {
			getLocations();
			setVisible(false);
		}
	}, [visible]);

	useEffect(() => {
		const params = Object.fromEntries(
			Object.entries(filters).filter(([, value]) => value !== ""),
		);
		router.push({ pathname: "/admin/location", query: params });
		setPageNumber(1);
		setLocations([]);
		setHasMore(true);
		onViewTypeChange(filters.status);
	}, [filters.search, filters.status]);

	/** Toggles the option to select all locations in the list
	 * @param {Event} event The HTML event that triggered this method
	 */
	const toggleSelectAll = event => {
		const {
			target: { checked },
		} = event;
		if (checked)
			setSelections(
				locations.map(({ LocationId, LocationName }) => ({
					LocationId,
					LocationName,
					Approved: true,
				})),
			);
		else setSelections([]);
	};
	/**
	 * Updates the status of a single selected location
	 * @param  {Number} LocationId The unique id of a specific location
	 * @param  {Number} LocationGroupId The unique group id of a specific location
	 * @param  {String} LocationName The name of a specific location
	 * @param  {Boolean} Approved The locations approval status
	 * @param  {Number} unassign Determines the unassigned status of the selected location
	 */
	const updateSingleStatus = (
		{ LocationId = null, LocationGroupId = null, LocationName, Approved },
		unassign = LOCATION_DENIAL_UNASSIGN_ACTIONS.DEFAULT,
	) => {
		const location = Object.fromEntries(
			Object.entries({ LocationId, LocationGroupId }).filter(
				([key, value]) => value !== null,
			),
		); // eslint-disable-line no-unused-vars

		JurisdictionLocationService.update({
			dataParams: {
				...location,
				LocationName,
				Approved,
			},
			queryParams: {
				unassign,
			},
		})
			.then(() => {
				getLocations();
				getCounts();

				setPageNumber(1);
				setLocations([]);
				setHasMore(true);

				utils.notify({
					type: "success",
					title: lang.t("Success"),
					message: Approved
						? lang.t("Location approved successfully")
						: lang.t("Location denied successfully"),
				});
			})
			.catch(({ response: { data } }) => {
				utils.notify({
					type: "error",
					message: data?.Message ?? lang.t("Error saving location"),
				});
			});
	};
	/**
	 * Updates the status of a list of selected locations
	 * @param  {Array} selectedLocations A list of selected location to be updated
	 * @param  {Number} unassign Determines the unassigned status of the selected locations
	 */
	const updateMultipleStatus = (
		selectedLocations,
		unassign = LOCATION_DENIAL_UNASSIGN_ACTIONS.DEFAULT,
	) => {
		const approved = selectedLocations[0].Approved;

		JurisdictionLocationService.updateMultiple({
			dataParams: selectedLocations,
			queryParams: {
				unassign,
			},
		})
			.then(() => {
				setSelections([]);
				getCounts();
				setLocations(
					locations.filter(
						loc =>
							selectedLocations.findIndex(sl => sl.LocationId === loc.LocationId) ===
							-1,
					),
				);
				utils.notify({
					type: "success",
					title: lang.t("Success"),
					message: approved
						? lang.t("Selected Locations approved successfully")
						: lang.t("Selected Locations denied successfully"),
				});
			})
			.catch(({ response: { data } }) => {
				utils.notify({
					type: "error",
					message: data?.Message ?? lang.t("Error saving location"),
					title: "Oops",
				});
			});
	};

	const onFilter = property => event => {
		// Only update the state if the incoming value is different than the current value
		if (event?.value !== filters[property] || property === "pageNumber") {
			const newFilters = { ...filters, [property]: event?.value ?? "" };
			setFilters(newFilters);
		}
	};

	const onNameSearch = value => {
		onFilter("search")({ value });
	};

	const onViewTypeChange = type => {
		const locationType = LOCATION_APPROVAL_TYPES_MAP.find(
			location => location.type === parseInt(type, 10),
		);
		onFilter("status")({ value: locationType?.type || 30 });
	};

	const onSelection =
		({ LocationId, LocationName, AnyAssignedOfficers, JobEventsWithAssignedOfficers }) =>
		event => {
			if (event) event.stopPropagation();

			const isSelected = selections.find(s => s.LocationId === LocationId);
			if (!isSelected) {
				setSelections([
					...selections,
					{
						LocationId,
						LocationName,
						AnyAssignedOfficers,
						JobEventsWithAssignedOfficers,
					},
				]);
			} else {
				setSelections(selections.filter(item => item.LocationId !== LocationId));
			}
		};

	const newLocationCallback = () => {
		setShowLocationModal(false);
		getCounts();
		onFilter("pageNumber")({ value: 1 });
	};

	/**
	 * Updates the status of the selected location
	 * @param {Object} item The selected location tp update
	 * @param {Boolean} Approved Determines which status the location will be updated to (Approved or Denied)
	 */
	const onUpdateStatus = (item, Approved) => {
		const {
			LocationId,
			LocationGroupId,
			LocationName,
			AnyAssignedOfficers,
			JobEventsWithAssignedOfficers,
		} = item;

		if (AnyAssignedOfficers && !Approved) {
			setModalSettings("locationApprovals", "locationDenialUnassign", {
				multiple: false,
				LocationId,
				LocationGroupId,
				LocationName,
				JobEventsWithAssignedOfficers,
			});
			setModalOpen("locationApprovals", "locationDenialUnassign", true);
			return;
		}

		updateSingleStatus({
			LocationId,
			LocationGroupId,
			LocationName,
			Approved,
		});
	};

	/**
	 * Updates the status the selected locations
	 * @param {Boolean} Approved Determines which status the selected locations will be updated to (Approved or Denied)
	 */
	const onUpdateMultipleStatus = Approved => () => {
		const selectedLocations = selections.map(({ LocationId, LocationName }) => ({
			LocationId,
			LocationName,
			Approved,
		}));
		const assignedOfficers = selections.some(item => item.AnyAssignedOfficers);
		const officersJobs = selections.reduce(
			(acc, item) => [...acc, ...(item.JobEventsWithAssignedOfficers ?? [])],
			[],
		);

		if (assignedOfficers && !Approved) {
			setModalSettings("locationApprovals", "locationDenialUnassign", {
				selectedLocations,
				multiple: true,
				JobEventsWithAssignedOfficers: officersJobs,
			});
			setModalOpen("locationApprovals", "locationDenialUnassign", true);
			return;
		}

		updateMultipleStatus(selectedLocations);
	};

	/**
	 * Builds an address for each location in the list
	 * @param {Object} item the location object
	 */
	const buildAddress = item =>
		utils.concatenateAddressDetails(
			{
				address: item.StreetAddress,
				city: item.City,
				state: item.State,
				zip: item.Zipcode,
				latitude: item.Latitude,
				longitude: item.Longitude,
			},
			{ useMultiline: true },
		);

	return (
		<div
			id="location-list"
			className="container"
		>
			<div className="row v-align page-title">
				<h2 className="col-xs-6">{lang.t("Location Management")}</h2>
				<div className="col-xs-6 text-right">
					<button
						type="button"
						name="new-location"
						className="btn btn-primary"
						onClick={() => setShowLocationModal(true)}
					>
						{`+ ${lang.t("New Location")}`}
					</button>
				</div>
			</div>

			<HeaderGrid
				gridOptions={LOCATION_APPROVAL_TYPES_MAP}
				loadingCounts={loadingCounts}
				counts={counts}
				activeGrid={filters.status}
				setActive={onViewTypeChange}
			/>

			<div className="row list-search">
				<div className="col-sm-4 col-md-5">
					<SearchField
						placeholder={lang.t("Search")}
						onSearch={onNameSearch}
						clearable
						defaultValue={filters.search}
					/>
				</div>
				<div className="col-sm-2 visible-xs visible-sm">
					<If
						condition={
							parseInt(filters.status, 10) === LOCATION_APPROVAL_TYPES.pending.type
						}
					>
						<div className="checkbox">
							<label htmlFor="selectAll">
								<input
									type="checkbox"
									id="selectAll"
									title={lang.t("Select All")}
									checked={
										locations.length !== 0 &&
										locations.length === selections.length
									}
									onChange={toggleSelectAll}
								/>
								{lang.t("Select All")}
							</label>
						</div>
					</If>
				</div>
			</div>

			<div className="list-header row hidden-xs hidden-sm v-align-md">
				<div
					className={
						parseInt(filters.status, 10) === LOCATION_APPROVAL_TYPES.pending.type
							? "col-md-3"
							: "col-md-4"
					}
				>
					<div className="row v-align-md">
						<If
							condition={
								parseInt(filters.status, 10) ===
								LOCATION_APPROVAL_TYPES.pending.type
							}
						>
							<div className="col-sm-2 checkbox">
								<label htmlFor="selectAll">
									<input
										type="checkbox"
										id="selectAll"
										checked={
											locations.length !== 0 &&
											locations.length === selections.length
										}
										onChange={toggleSelectAll}
										title={lang.t("Select All")}
									/>
								</label>
							</div>
						</If>
						<div
							className={
								parseInt(filters.status, 10) ===
								LOCATION_APPROVAL_TYPES.pending.type
									? "col-md-10"
									: "col-md-12"
							}
						>
							<strong>
								{lang.t(
									`${parseInt(filters.status, 10) === LOCATION_APPROVAL_TYPES.pending.type ? "Address" : "Location"}`,
								)}
							</strong>
						</div>
					</div>
				</div>
				<If
					condition={
						parseInt(filters.status, 10) === LOCATION_APPROVAL_TYPES.pending.type
					}
				>
					<div className="col-md-4">
						<strong>{lang.t("Agency")}</strong>
					</div>
				</If>
				<div
					className={
						parseInt(filters.status, 10) === LOCATION_APPROVAL_TYPES.pending.type
							? "col-md-2"
							: "col-md-4"
					}
				>
					<strong>{lang.t("Submitted")}</strong>
				</div>
			</div>

			<div className="list-body">
				<For
					each="item"
					of={locations}
				>
					<Link
						key={`location_${item.LocationId}_${item.LocationGroupId}`}
						to={`${item.LocationGroupId ? `/locationgroups/${item.LocationGroupId}` : `/locations/${item.LocationId}`}`}
					>
						<div className="row v-align-md">
							<div
								className={`col-xs-12 ${parseInt(filters.status, 10) === LOCATION_APPROVAL_TYPES.pending.type ? "col-md-3" : "col-md-4"}`}
							>
								<div className="row v-align-md">
									<If
										condition={
											parseInt(filters.status, 10) ===
											LOCATION_APPROVAL_TYPES.pending.type
										}
									>
										<div className="col-xs-12 col-md-2 checkbox">
											<label htmlFor={`location_list_${item.LocationId}`}>
												<input
													type="checkbox"
													checked={
														!!selections.find(
															s => s.LocationId === item.LocationId,
														)
													}
													onClick={e => e.stopPropagation()}
													onChange={onSelection(item)}
												/>
											</label>
										</div>
									</If>
									<div
										className={`col-xs-12 ${parseInt(filters.status, 10) === LOCATION_APPROVAL_TYPES.pending.type ? "col-md-10" : "col-md-12"}`}
									>
										<strong>{item.LocationName ?? ""}</strong>
										<div>{buildAddress(item).top}</div>
										<div>{buildAddress(item).bottom}</div>
									</div>
								</div>
								<br className="visible-xs" />
							</div>
							<If
								condition={
									parseInt(filters.status, 10) ===
									LOCATION_APPROVAL_TYPES.pending.type
								}
							>
								<div className="col-xs-7 col-xs-push-5 col-md-push-0 col-md-4">
									<div>
										<If condition={item.ByAgencyName}>
											<Image
												className="img-circle"
												src={item.ByAgencyLogoUrl}
												fallbackSrc="avatar"
											/>
											<span className="agency-name">
												{item.ByAgencyName ?? ""}
											</span>
										</If>
									</div>
								</div>
							</If>
							<div
								className={`col-xs-5 col-xs-pull-7 col-md-pull-0 ${
									parseInt(filters.status, 10) ===
									LOCATION_APPROVAL_TYPES.pending.type
										? "col-md-2"
										: "col-md-4"
								}`}
							>
								<If condition={item.SubmittedDate}>
									<strong className="visible-xs">{`${lang.t("Submitted")}: `}</strong>
									<div>
										{utils.formatDate(
											new Date(`${item.SubmittedDate}Z`),
											"en-US",
											user.TimeZoneId,
											"short",
										)}
									</div>
								</If>
							</div>
							<div
								className={`button-container col-xs-12 text-center ${
									parseInt(filters.status, 10) ===
									LOCATION_APPROVAL_TYPES.pending.type
										? "col-md-3"
										: "col-md-4"
								}`}
							>
								<If
									condition={
										parseInt(filters.status, 10) !==
										LOCATION_APPROVAL_TYPES.denied.type
									}
								>
									<button
										type="button"
										className="btn btn-danger"
										aria-label={lang.t("Deny")}
										onClick={e => {
											e.preventDefault();
											onUpdateStatus(item, false);
										}}
									>
										{lang.t("Deny")}
									</button>
								</If>
								<If
									condition={
										parseInt(filters.status, 10) !==
										LOCATION_APPROVAL_TYPES.approved.type
									}
								>
									<button
										type="button"
										className="btn btn-primary"
										aria-label={lang.t("Approve")}
										onClick={e => {
											e.preventDefault();
											onUpdateStatus(item, true);
										}}
									>
										{lang.t("Approve")}
									</button>
								</If>
							</div>
						</div>
					</Link>
				</For>
				<If condition={hasMore}>
					<ScrollDetect
						className="text-center"
						onVisible={() => setVisible(true)}
					>
						<i className="glyphicon glyphicon-repeat rkf-spin rkf-fw" />
					</ScrollDetect>
				</If>
			</div>

			{/* Bulk Invoice Actions */}
			<div className={`bulk-actions ${selections.length > 0 ? "show-actions" : ""}`}>
				<div className="row">
					<div className="col-xs-12 text-center">
						<button
							type="button"
							className="btn btn-default"
							onClick={onUpdateMultipleStatus(true)}
						>
							<span className="text-primary">{lang.t("Approve Selected")}</span>
						</button>
						<button
							type="button"
							className="btn btn-default"
							onClick={onUpdateMultipleStatus(false)}
						>
							<span className="text-danger">{lang.t("Deny Selected")}</span>
						</button>
					</div>
				</div>
			</div>
			{/* Bulk Invoice Actions */}
			<LocationDenialUnassign
				updateSingleStatus={updateSingleStatus}
				updateMultipleStatus={updateMultipleStatus}
			/>
			<LocationModal
				show={showLocationModal}
				onHide={() => setShowLocationModal(false)}
				onSuccess={newLocationCallback}
				title={lang.t("Add Location")}
				fromLocationApprovals
				user={user}
			/>
		</div>
	);
}

const mapDispatchToProps = dispatch => ({
	setModalOpen: bindActionCreators(Actions, dispatch).setModalOpen,
	setModalSettings: bindActionCreators(Actions, dispatch).setModalSettings,
});

LocationList.propTypes = {
	router: PropTypes.object.isRequired,
	user: PropTypes.object,
	setModalOpen: PropTypes.func.isRequired,
	setModalSettings: PropTypes.func.isRequired,
};

LocationList.defaultProps = {
	user: {},
};

export default connect(null, mapDispatchToProps)(LocationList);
